import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import BottomNav from '../BottomNav';
import AdminMetrics from './AdminMetrics';
import theme from '../theme';

const ADMIN_EMAIL = 'mattif56@gmail.com';

function Admin({ session }) {
  const navigate = useNavigate();
  const [section, setSection] = useState('verifiche');
  const [requests, setRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (session?.user?.email !== ADMIN_EMAIL) {
      navigate('/');
      return;
    }
    fetchRequests();
    fetchReports();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url, identity_status, identity_document_url')
      .eq('identity_status', 'in_verifica')
      .order('full_name');
    if (data) setRequests(data);
    setLoading(false);
  };

  const fetchReports = async () => {
    const { data } = await supabase
      .from('reports')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setReports(data);
  };

  const updateStatus = async (userId, status) => {
    await supabase.from('profiles')
      .update({ identity_status: status })
      .eq('id', userId);
    setRequests(prev => prev.filter(r => r.id !== userId));

    if (status === 'verificato') {
      const { sendPushNotification } = await import('../sendPushNotification');
      sendPushNotification({
        userId,
        title: '✅ Identità verificata!',
        body: 'Il tuo documento è stato approvato. Ora puoi candidarti e pubblicare annunci su Jobbetty.',
        url: '/profilo',
      });
    } else if (status === 'non_verificato') {
      const { sendPushNotification } = await import('../sendPushNotification');
      sendPushNotification({
        userId,
        title: '❌ Verifica identità non approvata',
        body: 'Il documento caricato non è stato accettato. Riprova dal tuo profilo.',
        url: '/profilo',
      });
    }
  };

  const markReportAsRead = async (reportId) => {
    await supabase.from('reports').update({ status: 'letto' }).eq('id', reportId);
    setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'letto' } : r));
  };

  const getSignedUrl = async (path) => {
    const { data } = await supabase.storage
      .from('identity-docs')
      .createSignedUrl(path, 60);
    return data?.signedUrl || null;
  };

  const handleViewDocs = async (req) => {
    const docPath = req.identity_document_url;
    if (!docPath) return;

    const paths = docPath.includes('|') ? docPath.split('|') : [docPath];
    const urls = await Promise.all(paths.map(p => getSignedUrl(p)));
    const validUrls = urls.filter(Boolean);

    if (validUrls.length > 0) {
      setLightbox({ urls: validUrls, index: 0 });
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  if (session?.user?.email !== ADMIN_EMAIL) return null;

  const newReportsCount = reports.filter(r => r.status === 'nuovo').length;

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family }}>
      <div style={{ background: theme.colors.primary, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        <button onClick={() => navigate('/profilo')} style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <h2 style={{ margin: 0, color: theme.colors.white, fontSize: theme.font.size.xxl }}>🛡️ Pannello Admin</h2>
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${theme.colors.border}`, background: theme.colors.white, overflowX: 'auto' }}>
        <button onClick={() => setSection('verifiche')} style={{
          flex: '1 0 auto', padding: 14, border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
          borderBottom: section === 'verifiche' ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
          color: section === 'verifiche' ? theme.colors.primary : theme.colors.textMuted,
          fontWeight: section === 'verifiche' ? 700 : 400, fontSize: theme.font.size.base,
        }}>🪪 Verifiche {requests.length > 0 && `(${requests.length})`}</button>
        <button onClick={() => setSection('segnalazioni')} style={{
          flex: '1 0 auto', padding: 14, border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
          borderBottom: section === 'segnalazioni' ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
          color: section === 'segnalazioni' ? theme.colors.primary : theme.colors.textMuted,
          fontWeight: section === 'segnalazioni' ? 700 : 400, fontSize: theme.font.size.base,
        }}>🚩 Segnalazioni {newReportsCount > 0 && `(${newReportsCount})`}</button>
        <button onClick={() => setSection('metriche')} style={{
          flex: '1 0 auto', padding: 14, border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
          borderBottom: section === 'metriche' ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
          color: section === 'metriche' ? theme.colors.primary : theme.colors.textMuted,
          fontWeight: section === 'metriche' ? 700 : 400, fontSize: theme.font.size.base,
        }}>📊 Metriche</button>
      </div>

      <div style={{ padding: theme.spacing.xl, paddingBottom: 80 }}>

        {section === 'verifiche' && (
          <>
            {loading && <p style={{ textAlign: 'center', color: theme.colors.textMuted }}>Caricamento...</p>}

            {!loading && requests.length === 0 && (
              <div style={{ textAlign: 'center', marginTop: 60 }}>
                <div style={{ fontSize: 48, marginBottom: theme.spacing.lg }}>✅</div>
                <p style={{ color: theme.colors.textMuted }}>Nessuna richiesta di verifica in attesa.</p>
              </div>
            )}

            {requests.map(req => (
              <div key={req.id} style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.md, background: theme.colors.white }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: theme.radius.circle, background: theme.colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: theme.colors.primary, fontSize: theme.font.size.md, overflow: 'hidden', flexShrink: 0 }}>
                    {req.avatar_url
                      ? <img src={req.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : req.full_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: theme.font.size.lg }}>{req.full_name}</div>
                    <div style={{ fontSize: theme.font.size.sm, color: theme.colors.warningText, fontWeight: 600 }}>⏳ In verifica</div>
                  </div>
                </div>

                <button onClick={() => handleViewDocs(req)} style={{
                  width: '100%', background: theme.colors.info, color: theme.colors.white,
                  border: 'none', borderRadius: theme.radius.sm, padding: '10px',
                  fontSize: theme.font.size.md, fontWeight: 600, cursor: 'pointer', marginBottom: 10
                }}>
                  🪪 Visualizza documento {req.identity_document_url?.includes('|') ? '(fronte + retro)' : '(1 foto)'}
                </button>

                <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                  <button onClick={() => updateStatus(req.id, 'verificato')} style={{
                    flex: 1, background: theme.colors.primary, color: theme.colors.white,
                    border: 'none', borderRadius: theme.radius.sm, padding: '10px',
                    fontSize: theme.font.size.md, fontWeight: 700, cursor: 'pointer'
                  }}>✅ Approva</button>
                  <button onClick={() => updateStatus(req.id, 'non_verificato')} style={{
                    flex: 1, background: theme.colors.white, color: theme.colors.errorDark,
                    border: `1px solid ${theme.colors.errorDark}`, borderRadius: theme.radius.sm, padding: '10px',
                    fontSize: theme.font.size.md, fontWeight: 700, cursor: 'pointer'
                  }}>❌ Rifiuta</button>
                </div>
              </div>
            ))}
          </>
        )}

        {section === 'segnalazioni' && (
          <>
            {reports.length === 0 && (
              <div style={{ textAlign: 'center', marginTop: 60 }}>
                <div style={{ fontSize: 48, marginBottom: theme.spacing.lg }}>📭</div>
                <p style={{ color: theme.colors.textMuted }}>Nessuna segnalazione ricevuta.</p>
              </div>
            )}

            {reports.map(report => (
              <div key={report.id} style={{
                border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg,
                padding: theme.spacing.lg, marginBottom: theme.spacing.md,
                background: report.status === 'nuovo' ? theme.colors.primaryLight : theme.colors.white,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing.sm }}>
                  <span style={{
                    background: report.type === 'problema' ? theme.colors.errorBg : theme.colors.infoBg,
                    color: report.type === 'problema' ? theme.colors.errorDark : theme.colors.info,
                    padding: '3px 10px', borderRadius: theme.radius.pill, fontSize: theme.font.size.sm, fontWeight: 600,
                  }}>
                    {report.type === 'problema' ? '⚠️ Problema' : '💡 Suggerimento'}
                  </span>
                  {report.status === 'nuovo' && (
                    <span style={{ background: theme.colors.error, color: theme.colors.white, padding: '2px 8px', borderRadius: theme.radius.pill, fontSize: theme.font.size.xs, fontWeight: 700 }}>Nuovo</span>
                  )}
                </div>
                <p style={{ margin: '0 0 10px', fontSize: theme.font.size.md, color: theme.colors.textPrimary, lineHeight: 1.5 }}>
                  {report.description}
                </p>
                <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, marginBottom: report.status === 'nuovo' ? theme.spacing.sm : 0 }}>
                  👤 {report.user_name || report.user_email} · {formatDate(report.created_at)}
                </div>
                {report.status === 'nuovo' && (
                  <button onClick={() => markReportAsRead(report.id)} style={{
                    background: 'none', border: `1px solid ${theme.colors.primary}`, color: theme.colors.primary,
                    borderRadius: theme.radius.sm, padding: '6px 14px', fontSize: theme.font.size.sm, cursor: 'pointer',
                  }}>✓ Segna come letta</button>
                )}
              </div>
            ))}
          </>
        )}

        {section === 'metriche' && <AdminMetrics />}

      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.95)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: theme.spacing.lg
        }}>
          <div style={{ position: 'absolute', top: 16, color: theme.colors.white, fontSize: theme.font.size.base, fontWeight: 600 }}>
            {lightbox.index + 1} / {lightbox.urls.length} — {lightbox.index === 0 ? 'Fronte' : 'Retro'}
          </div>

          {lightbox.urls.length > 1 && lightbox.index > 0 && (
            <button onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, index: l.index - 1 })); }}
              style={{ position: 'absolute', left: 16, background: 'rgba(255,255,255,0.2)', border: 'none', color: theme.colors.white, fontSize: 28, cursor: 'pointer', borderRadius: theme.radius.circle, width: 44, height: 44 }}>‹</button>
          )}

          <img
            src={lightbox.urls[lightbox.index]}
            alt={lightbox.index === 0 ? 'fronte' : 'retro'}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90%', maxHeight: '70vh', objectFit: 'contain', borderRadius: theme.radius.sm }}
          />

          {lightbox.urls.length > 1 && lightbox.index < lightbox.urls.length - 1 && (
            <button onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, index: l.index + 1 })); }}
              style={{ position: 'absolute', right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', color: theme.colors.white, fontSize: 28, cursor: 'pointer', borderRadius: theme.radius.circle, width: 44, height: 44 }}>›</button>
          )}

          <button onClick={() => setLightbox(null)} style={{
            background: theme.colors.white, color: '#333', border: 'none', borderRadius: theme.radius.sm,
            padding: '10px 24px', fontSize: theme.font.size.md, fontWeight: 600, cursor: 'pointer'
          }}>Chiudi</button>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

export default Admin;